import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Landing page for a tab opened by the superadmin role switcher.
 *
 * The switcher links here with just a role name - never a credential. This tab
 * still has the superadmin's own (shared, localStorage) session when it loads,
 * so it asks the server for the preview session itself and then swaps to it in
 * tab-scoped storage. Nothing sensitive ever appears in the URL, and the
 * superadmin's session in other tabs is left untouched.
 */
const ImpersonationCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { beginImpersonation, user, token, initializing } = useAuth();
  const [error, setError] = useState('');
  // React StrictMode double-invokes effects in development; without this guard
  // the session would be requested twice.
  const startedRef = useRef(false);

  useEffect(() => {
    // Wait for the stored session to be restored before deciding anything.
    if (initializing) return;
    if (startedRef.current) return;
    startedRef.current = true;

    const role = searchParams.get('role');

    if (!role) {
      setError('No role was specified.');
      return;
    }
    if (!token || !user) {
      setError('You need to be signed in as a superadmin to open a role dashboard.');
      return;
    }
    if (user.role !== 'superadmin') {
      setError('Only a superadmin can open other role dashboards.');
      return;
    }

    (async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/superadmin/impersonation/session`,
          { role },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const { token: previewToken, user: previewUser, dashboardPath, impersonation } = res.data;
        if (!previewToken || !previewUser) {
          setError('The server did not return a usable session.');
          return;
        }

        beginImpersonation({ token: previewToken, user: previewUser, impersonation });
        navigate(dashboardPath || `/${previewUser.role}/dashboard`, { replace: true });
      } catch (err) {
        setError(
          err.response?.data?.message ||
          `Could not open the ${role} dashboard.`
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializing]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 max-w-md w-full text-center">
          <FaExclamationTriangle className="h-8 w-8 mx-auto mb-4 text-amber-500" />
          <h1 className="text-lg font-semibold text-gray-800 mb-2">
            Could not open this dashboard
          </h1>
          <p className="text-sm text-gray-600 mb-6">{error}</p>
          <Link
            to="/superadmin/dashboard"
            className="inline-block text-sm font-semibold text-[#0077B6] hover:underline"
          >
            Back to the superadmin dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-[#0077B6]" />
      <p className="text-sm text-gray-500">Opening dashboard&hellip;</p>
    </div>
  );
};

export default ImpersonationCallback;
